import { CSSObject } from '@emotion/react'
import classNames from 'classnames'
import { forwardRef } from 'react'
import { Size } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { WithTheme } from '../../gem'
import { Button, ButtonSize } from '../Button'
import { Icon } from '../Icon'
import * as styles from './styles'

type Id = string | number

export type TokenProps = WithTheme &
  Omit<
    React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    >,
    'ref'
  > & {
    /** A 16px mark in front of the text: a channel, an avatar. */
    leadingNode?: React.ReactNode
    /**
     * A quiet word in front of the text, for a value that needs its context to
     * be read: the label set a label belongs to, or which end of a range a
     * date is.
     */
    leadingText?: string
    trailingNode?: React.ReactNode
    /**
     * A quiet phrase after the text that qualifies it: "except 2 accounts".
     * It keeps its own box, so a long value is clipped before the phrase is.
     */
    trailingText?: string
    value: Id
    onRemoveToken?: (value: Id) => void
    onSearch?: (value: string) => void
    placeholderToken?: boolean
    removable?: boolean
    searchable?: boolean
    searchFocused?: boolean
    /** @deprecated */
    styleOverrides?: CSSObject
    text?: string
  }

export const Token = forwardRef(
  (props: TokenProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    const {
      className,
      leadingNode,
      leadingText,
      trailingNode,
      trailingText,
      value,
      onRemoveToken,
      onSearch,
      placeholderToken = false,
      removable = false,
      searchable = false,
      searchFocused = false,
      styleOverrides,
      text,
      theme: providedTheme,
      ...rest
    } = props

    const innerTheme = useTheme()
    const theme = providedTheme ?? innerTheme
    const hasPlus = placeholderToken || searchable
    return (
      <div
        className={classNames(['lapidary-token', className])}
        css={[
          styles.base({
            theme,
            providedTheme,
            searchable,
            removable,
            placeholder: placeholderToken,
            leading: hasPlus || Boolean(leadingNode),
            inputFocused: searchFocused,
            interactive: Boolean(searchable || removable || placeholderToken),
          }),
          styleOverrides,
        ]}
      >
        {hasPlus && (
          <Icon
            glyph="gem-add"
            size={Size.Icon}
            styleOverrides={{
              icon: styles.iconColor(theme, searchable),
            }}
            ariaHidden
          />
        )}
        {leadingNode}
        {leadingText && !searchable && (
          <span
            className="lapidary-token-leading-text"
            css={styles.leadingText(providedTheme)}
          >
            {leadingText}
          </span>
        )}
        {text && !searchable && (
          <span className="lapidary-token-text" css={styles.text}>
            {text}
          </span>
        )}
        {trailingText && !searchable && (
          <>
            {' '}
            <span
              className="lapidary-token-trailing-text"
              css={styles.trailingText(providedTheme)}
            >
              {trailingText}
            </span>
          </>
        )}
        {searchable && (
          <input
            onChange={(e) => onSearch?.(e.target.value)}
            {...rest}
            ref={ref}
            css={styles.input(theme, searchFocused)}
            autoComplete="off"
            type="text"
            name="Search token options"
            placeholder={searchFocused ? 'Type to search...' : 'Add'}
          />
        )}
        {trailingNode}
        {removable && !searchable && (
          <Button
            ariaLabel={['Remove', leadingText, text].filter(Boolean).join(' ')}
            name="remove token"
            variant="ghost"
            size={ButtonSize.Small}
            icon={
              <Icon
                glyph="gem-close-small"
                size={Size.Icon}
                styleOverrides={{ container: styles.iconPosition }}
                ariaHidden
              />
            }
            onClick={() => onRemoveToken?.(value)}
            styleOverrides={{ button: styles.buttonOverrides }}
          />
        )}
      </div>
    )
  }
)
Token.displayName = 'Lapidary: Token'
